import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react'

interface Etapa7Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

export function Etapa7MobilidadeRisco({ formData, onChange, onNext, onPrev, onSubmit, saving, submitting }: Etapa7Props) {
  const mobilidade = formData.mobilidade || {}

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    // Validar declaração de veracidade
    if (!formData.veracidade_etapa7) {
      errors.push('Declaração de veracidade')
    }

    // Validar apoio à mobilidade
    if (!mobilidade.usa_bengala && !mobilidade.usa_andador && !mobilidade.cadeira_rodas && !mobilidade.acamada && !mobilidade.claudica && !mobilidade.mobilidade_normal) {
      errors.push('Selecione pelo menos uma opção em Apoio à Mobilidade')
    }

    // Validar riscos
    if (!mobilidade.quedas && !mobilidade.oxigenio && !mobilidade.feridas && !mobilidade.broncoaspiracao && !mobilidade.sem_quedas_lesoes_riscos) {
      errors.push('Selecione pelo menos uma opção em Riscos')
    }

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  const handleMobilidadeChange = (campo: string, valor: boolean) => {
    onChange('mobilidade', {
      ...mobilidade,
      [campo]: valor
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Mobilidade e Risco
          </CardTitle>
          <p className="text-sm text-gray-600">
            Avalie os fatores de mobilidade e risco do idoso(a).
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold">Apoio à Mobilidade</h4>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="usa_bengala"
                  checked={mobilidade.usa_bengala || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('usa_bengala', checked as boolean)}
                />
                <Label htmlFor="usa_bengala">
                  Usa bengala
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="usa_andador"
                  checked={mobilidade.usa_andador || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('usa_andador', checked as boolean)}
                />
                <Label htmlFor="usa_andador">
                  Usa andador
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cadeira_rodas"
                  checked={mobilidade.cadeira_rodas || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('cadeira_rodas', checked as boolean)}
                />
                <Label htmlFor="cadeira_rodas">
                  Usa cadeira de rodas
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="acamada"
                  checked={mobilidade.acamada || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('acamada', checked as boolean)}
                />
                <Label htmlFor="acamada">
                  Acamado(a)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="claudica"
                  checked={mobilidade.claudica || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('claudica', checked as boolean)}
                />
                <Label htmlFor="claudica">
                  Claudica (anda mancando)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="mobilidade_normal"
                  checked={mobilidade.mobilidade_normal || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('mobilidade_normal', checked as boolean)}
                />
                <Label htmlFor="mobilidade_normal">
                  Mobilidade normal
                </Label>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold">Fatores de Risco</h4>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="quedas"
                  checked={mobilidade.quedas || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('quedas', checked as boolean)}
                />
                <Label htmlFor="quedas">
                  Histórico de quedas
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="oxigenio"
                  checked={mobilidade.oxigenio || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('oxigenio', checked as boolean)}
                />
                <Label htmlFor="oxigenio">
                  Usa oxigênio
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="feridas"
                  checked={mobilidade.feridas || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('feridas', checked as boolean)}
                />
                <Label htmlFor="feridas">
                  Lesões por pressão/feridas
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="broncoaspiracao"
                  checked={mobilidade.broncoaspiracao || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('broncoaspiracao', checked as boolean)}
                />
                <Label htmlFor="broncoaspiracao">
                  Risco de broncoaspiração
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sem_quedas_lesoes_riscos"
                  checked={mobilidade.sem_quedas_lesoes_riscos || false}
                  onCheckedChange={(checked) => handleMobilidadeChange('sem_quedas_lesoes_riscos', checked as boolean)}
                />
                <Label htmlFor="sem_quedas_lesoes_riscos">
                  Sem quedas, lesões, riscos ou outros
                </Label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="observacoes_mobilidade">Observações adicionais</Label>
            <Textarea
              id="observacoes_mobilidade"
              value={mobilidade.observacoes || ''}
              onChange={(e) => onChange('mobilidade', {
                ...mobilidade,
                observacoes: e.target.value
              })}
              placeholder="Descreva outros aspectos de mobilidade ou risco relevantes"
              className="mt-1"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termo de Veracidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Declaro que as informações prestadas nesta avaliação funcional são verdadeiras e 
              foram fornecidas com base no meu conhecimento sobre a capacidade do idoso(a).
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="veracidade_etapa7"
                checked={formData.veracidade_etapa7 || false}
                onCheckedChange={(checked) => onChange('veracidade_etapa7', checked as boolean)}
                className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
              />
              <Label htmlFor="veracidade_etapa7" className="text-sm font-medium">
                Declaro que as informações são verdadeiras *
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
